import * as Sentry from '@sentry/react'
import type { MetricAttributes } from '../Telemetry/Telemetry.ts'
import { getTelemetryOrg, trackMetric } from '../Telemetry/Telemetry.ts'
import { recordWholeSession } from '../Telemetry/Replay.ts'
import type { DetectedSequencingPlatform } from '../../Util/sequencingPlatformDetection.ts'
import type { ReadLayout, ReadLayoutConfidence } from '../../Util/orderSettingDetection/readLayoutDetection.ts'
import type { PrimerTrimVerdict } from '../../Util/orderSettingDetection/primerTrimDetection.ts'
import type { MultiplexVerdict } from '../../Util/orderSettingDetection/multiplexDetection.ts'
import type { SampleConfigurationTypeKey } from '../../Generated/SampleConfigurationType.ts'
import type { PrimerDiscovery, SettingSource, SheetMapping, SheetMetadataField } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import type { SupportReason } from './sheetSupport.ts'
import type { PasteRefusal } from './primerPaste.ts'
import type { UploadFailure } from './uploadManager.ts'

/*
 * The wizard's per-step debugging summaries. Categories and counts only: this file's schema is the
 * whitelist, and anything outside it is dropped before it reaches Sentry, so a file name, sample name,
 * sequence, header or cell can never ride along.
 */

export type AnalysisStep = 'detection' | 'primer_estimate' | 'sheet' | 'paste' | 'upload'

const COUNT = 'count'
const FLAG = 'flag'
type Rule = typeof COUNT | typeof FLAG | readonly string[] | { readonly set: readonly string[] }

const PLATFORMS = ['ILLUMINA', 'ONT', 'UNKNOWN'] as const satisfies readonly DetectedSequencingPlatform[]
const LAYOUTS = ['PAIRED', 'MERGED_OR_SINGLE', 'UNKNOWN'] as const satisfies readonly ReadLayout[]
const LAYOUT_CONFIDENCES = ['high', 'names-only', 'none'] as const satisfies readonly ReadLayoutConfidence[]
const TRIM_VERDICTS = ['TRIMMED', 'UNTRIMMED', 'UNKNOWN'] as const satisfies readonly PrimerTrimVerdict[]
const MULTIPLEX_VERDICTS = ['MULTIPLEXED', 'SINGLE_SAMPLE', 'UNKNOWN'] as const satisfies readonly MultiplexVerdict[]
const CONFIGURATIONS = [
  'SINGLE_SAMPLE_PAIRS',
  'COMBINED_SAMPLE_PAIRS',
  'MERGED_AND_TRIMMED_SAMPLES',
  'MERGED_SAMPLES',
  'TRIMMED_SAMPLES'
] as const satisfies readonly SampleConfigurationTypeKey[]
const SETTING_SOURCES = ['detected', 'fallback', 'default', 'answered', 'entered', 'undetermined'] as const satisfies readonly SettingSource[]
const PRIMER_STATUSES = ['found', 'mixed', 'partial', 'none', 'registry-unavailable'] as const satisfies readonly PrimerDiscovery['status'][]
export const SHEET_ROLES = [
  'fileForward',
  'fileReverse',
  'sampleId',
  'sampleName',
  'forwardPrimer',
  'reversePrimer',
  'forwardTag',
  'reverseTag',
  'itemType',
  'pool'
] as const satisfies readonly (keyof SheetMapping)[]
export const SHEET_METADATA_FIELDS = [
  'coordinatesMerged',
  'coordinatesLat',
  'coordinatesLon',
  'samplingDate',
  'samplingStart',
  'samplingEnd',
  'matrixType',
  'extractionKit',
  'trapType',
  'collectionType',
  'sequencer',
  'sequencingChemistry',
  'sequencingDepth',
  'localityType',
  'habitatType'
] as const satisfies readonly SheetMetadataField[]
const MATCH_METHODS = ['file', 'sample_id', 'sample_name', 'mixed', 'none'] as const
const SUPPORT_REASONS = ['parse-failed', 'no-key-column', 'low-match', 'ok'] as const satisfies readonly (SupportReason | 'ok')[]
const PASTE_REFUSALS = [
  'empty',
  'no_matching_rows',
  'ids_dont_match_files',
  'no_primers',
  'no_key_column',
  'not_primers',
  'nothing_landed'
] as const satisfies readonly PasteRefusal[]
const UPLOAD_FAILURES = ['http', 'network', 'aborted', 'unconfirmed', 'already_held', 'other'] as const satisfies readonly UploadFailure[]

export const ANALYSIS_FIELDS = {
  detection: {
    outcome: ['completed', 'failed'],
    classified: FLAG,
    files: COUNT,
    rejected: COUNT,
    samples: COUNT,
    platform: PLATFORMS,
    layout: LAYOUTS,
    layout_confidence: LAYOUT_CONFIDENCES,
    trimming: TRIM_VERDICTS,
    multiplexing: MULTIPLEX_VERDICTS,
    configuration: [...CONFIGURATIONS, 'UNKNOWN'],
    confidence: ['high', 'medium', 'low', 'none'],
    primers: PRIMER_STATUSES,
    files_scanned: COUNT,
    runs: COUNT,
    median_read_length: COUNT,
    sample_configuration: CONFIGURATIONS,
    configuration_source: SETTING_SOURCES
  },
  primer_estimate: {
    samples: COUNT,
    forward_found: COUNT,
    forward_none: COUNT,
    forward_ambiguous: COUNT,
    reverse_found: COUNT,
    reverse_none: COUNT,
    reverse_ambiguous: COUNT,
    failed: COUNT,
    tagged: COUNT,
    lab_spelling: COUNT,
    confidence_high: COUNT,
    confidence_low: COUNT,
    confidence_none: COUNT,
    expects_primers: FLAG
  },
  sheet: {
    outcome: ['loaded', 'remapped', 'unreadable'],
    source: ['file', 'paste'],
    roles: { set: SHEET_ROLES },
    metadata: { set: SHEET_METADATA_FIELDS },
    columns: COUNT,
    rows_total: COUNT,
    rows_matched: COUNT,
    rows_ambiguous: COUNT,
    samples: COUNT,
    match_method: MATCH_METHODS,
    support: SUPPORT_REASONS
  },
  paste: {
    kind: ['keyed', 'positional', 'unreadable'],
    refusal: PASTE_REFUSALS,
    rows_total: COUNT,
    rows_matched: COUNT,
    primers: COUNT,
    invalid_cells: COUNT,
    rows_left_out: COUNT,
    cells_left_out: COUNT,
    header_skipped: FLAG,
    match_method: MATCH_METHODS,
    low_match: FLAG,
    metadata: FLAG,
    sheet_like: FLAG
  },
  upload: {
    outcome: ['succeeded', 'retrying', 'failed'],
    failure: UPLOAD_FAILURES,
    http_status: COUNT,
    retries: COUNT
  }
} as const satisfies Record<AnalysisStep, Record<string, Rule>>

type Fields = typeof ANALYSIS_FIELDS
type ValueOf<R> = R extends typeof COUNT
  ? number
  : R extends typeof FLAG
    ? boolean
    : R extends { readonly set: readonly (infer S)[] }
      ? readonly S[]
      : R extends readonly (infer E)[]
        ? E
        : never

export type SummaryOf<S extends AnalysisStep> = { step: S } & { -readonly [K in keyof Fields[S]]?: ValueOf<Fields[S][K]> }
export type AnalysisSummary = { [S in AnalysisStep]: SummaryOf<S> }[AnalysisStep]

const OTHER = 'other'

const cleanValue = (rule: Rule, value: unknown): string | number | boolean | undefined => {
  if (rule === COUNT) {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.round(value) : undefined
  }
  if (rule === FLAG) {
    return typeof value === 'boolean' ? value : undefined
  }
  if (Array.isArray(rule)) {
    return typeof value === 'string' ? (rule.includes(value) ? value : OTHER) : undefined
  }
  const members = (rule as { set: readonly string[] }).set
  if (!Array.isArray(value)) {
    return typeof value === 'string' ? OTHER : undefined
  }
  const known = members.filter((member) => value.includes(member))
  const unknown = value.some((member) => typeof member !== 'string' || !members.includes(member))
  return [...known, ...(unknown ? [OTHER] : [])].join(',')
}

/** The summary as it may leave the browser: schema fields only, enum values or `other`, counts and flags. */
export const sanitizeSummary = (summary: AnalysisSummary): MetricAttributes => {
  const fields: Record<string, Rule> | undefined = ANALYSIS_FIELDS[summary.step]
  const clean: MetricAttributes = { step: summary.step }
  if (!fields) {
    return clean
  }
  for (const [field, value] of Object.entries(summary)) {
    const rule = fields[field]
    const kept = rule && value !== undefined ? cleanValue(rule, value) : undefined
    if (kept !== undefined) {
      clean[field] = kept
    }
  }
  return clean
}

/** Whether the summary passes the guard untouched: no unknown field, no value outside its enum. */
export const conformsToSchema = (summary: AnalysisSummary): boolean => {
  const fields: Record<string, Rule> | undefined = ANALYSIS_FIELDS[summary.step]
  if (!fields) {
    return false
  }
  return Object.entries(summary).every(([field, value]) => {
    if (field === 'step' || value === undefined) {
      return true
    }
    const rule = fields[field]
    const kept = rule ? cleanValue(rule, value) : undefined
    return kept !== undefined && kept !== OTHER && !String(kept).split(',').includes(OTHER)
  })
}

/** One step's summary, as a breadcrumb (so it shows on any later event and in the replay) and an `order_wizard.analysis` count. */
export const reportAnalysis = (summary: AnalysisSummary, { breadcrumb = true }: { breadcrumb?: boolean } = {}): void => {
  const data = sanitizeSummary(summary)
  if (breadcrumb) {
    try {
      Sentry.addBreadcrumb({ category: 'order_wizard', level: 'info', message: `${summary.step} analysed`, data })
    } catch {
      /* telemetry must never throw into the wizard */
    }
  }
  trackMetric('order_wizard.analysis', data)
}

export type WizardWarning = 'paste_unreadable' | 'primers_not_found' | 'upload_failed'

const WARNING_MESSAGES: Record<WizardWarning, string> = {
  paste_unreadable: 'Order wizard: a pasted block could not be read',
  primers_not_found: 'Order wizard: no primers found on files that should carry them',
  upload_failed: 'Order wizard: an upload failed for good'
}

const warned = new Set<string>()

/** A warning event for a failure the wizard shows as "nothing found", at most once per draft and kind. */
export const warnOnce = (draftId: string, kind: WizardWarning, summary: AnalysisSummary): void => {
  const id = `${draftId}\u0000${kind}`
  if (warned.has(id)) {
    return
  }
  warned.add(id)
  const org = getTelemetryOrg()
  try {
    Sentry.captureMessage(WARNING_MESSAGES[kind], {
      level: 'warning',
      tags: {
        order_wizard_warning: kind,
        ...(org ? { org_id: org.id, org_type: org.type, is_internal: String(org.isInternal) } : {})
      },
      contexts: { order_wizard: sanitizeSummary(summary) }
    })
  } catch {
    /* telemetry must never throw into the wizard */
  }
  /* a message carries no exception, so it never triggers replay-on-error: keep the session recorded instead */
  recordWholeSession()
}

export const forgetWarnings = (): void => warned.clear()
